import { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import SectionHeader from '@/components/ui/SectionHeader';
import NavArrow from '@/components/ui/NavArrow';
import { NEWS_POSTS, type NewsDomain } from '@/data/newsData';
import s from './News.module.scss';

// 뉴스 & 소식 — 가로 캐러셀(무한 순환) 미리보기. 클릭 시 상세(/news/:slug).
//   [2026-09-07] 데이터 = NEWS_POSTS(블로그 캡처 기반). 대표이미지 = 각 글 첫 AI 이미지 uncut(다 보이게, contain).
//   무한 루프: 카드 ×3 복제, 중간 세트 시작, 스크롤 멎으면 중간으로 순간이동(이음새 없음).
//   변경 금지 트리거: STEP(카드폭+gap)을 바꾸면 루프 주기(setWidth) 재계산 필요.
const STEP = 424; // 카드 400 + gap 24

const News = () => {
  // 안전/환경 도메인 탭(2026-09-08). 디폴트=안전. 사이트 2대 축(안전=주력제품 / 환경=대기배출 IoT).
  const [domain, setDomain] = useState<NewsDomain>('safety');
  const counts = useMemo(() => ({
    safety: NEWS_POSTS.filter((n) => n.domain === 'safety').length,
    environment: NEWS_POSTS.filter((n) => n.domain === 'environment').length,
  }), []);
  const news = useMemo(() => NEWS_POSTS.filter((n) => n.domain === domain), [domain]);
  const reveal = useReveal<HTMLDivElement>();
  const trackRef = useRef<HTMLDivElement>(null);

  const loopItems = [...news, ...news, ...news]; // ×3 (앞·중간·뒤 버퍼)
  const setWidth = news.length * STEP;            // 한 세트 주기(도메인 전환 시 재계산 → 중간 세트로 리셋)

  // 중간 세트에서 시작
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = setWidth;
  }, [setWidth]);

  // 스크롤이 멎으면(디바운스) 중간 세트 범위 [setWidth, 2*setWidth)로 정규화 → 무한 순환
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let t: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const sl = el.scrollLeft;
        if (sl < setWidth) el.scrollLeft = sl + setWidth;          // 왼쪽 끝 → 중간으로
        else if (sl >= setWidth * 2) el.scrollLeft = sl - setWidth; // 오른쪽 끝 → 중간으로
      }, 130);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, [setWidth]);

  const scrollByCard = (dir: number) =>
    trackRef.current?.scrollBy({ left: dir * STEP, behavior: 'smooth' });

  return (
    <section className={s.section}>
      <div className={s.inner}>
        <SectionHeader
          title="뉴스 & 소식"
          actions={<>
            <NavArrow dir="prev" onClick={() => scrollByCard(-1)} />
            <NavArrow dir="next" onClick={() => scrollByCard(1)} />
          </>}
        />
        {/* 안전/환경 세그먼트 토글 — 디폴트 안전. 활성 탭 = 흰 배경, 카운트 색상(안전 시안/환경 그린) */}
        <div className={s.tabs} role="tablist" aria-label="뉴스 카테고리">
          <button type="button" role="tab" aria-selected={domain === 'safety'}
            className={`${s.tab} ${domain === 'safety' ? s.on : ''}`} onClick={() => setDomain('safety')}>
            안전 <span>{counts.safety}</span>
          </button>
          <button type="button" role="tab" aria-selected={domain === 'environment'}
            className={`${s.tab} ${s.envTab} ${domain === 'environment' ? s.on : ''}`} onClick={() => setDomain('environment')}>
            환경 <span>{counts.environment}</span>
          </button>
        </div>
      </div>
      {/* 트랙은 풀폭(뷰포트 우측 끝까지) — padding-left로 첫 카드를 컨테이너 좌측에 정렬 */}
      <div className={s.viewport} ref={reveal}>
        <div className={s.track} ref={trackRef}>
          {loopItems.map((n, i) => (
            <Link key={`${n.slug}-${i}`} to={`/news/${n.slug}`} className={`${s.card} anim-scroll`}>
              <div className={s.media}>
                {n.cover
                  ? <img className={s.img} src={n.cover} alt={n.title} loading="lazy" />
                  : <div className={s.ph}><i className="ti ti-photo" aria-hidden /></div>}
              </div>
              <span className={`${s.cat} ${n.domain === 'environment' ? s.env : ''}`}>{n.domain === 'safety' ? '안전' : '환경'} · {n.category}</span>
              {/* 대표이미지(첫 AI 이미지)에 제목이 박혀 있어 중복 → 헤드라인은 화면에서 숨기고 접근성/SEO용으로만 유지 */}
              <h3 className={s.srOnly}>{n.title}</h3>
              <span className={s.date}>{n.date}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default News;
